import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../api/base';
import { getLocalized } from '../utils/localized';

interface Stats {
  totalTrees: number;
  totalPlanted: number;
  totalDonors: number;
}

interface Donor {
  id: number;
  organizationName?: string | null;
  firstName: string | null;
  lastName: string | null;
  instagramUrl?: string;
  treesCount: number;
  paidAt: string;
  isAnonymous?: boolean;
}

interface Festival {
  id: number;
  slug: string;
  name: { ru?: string; ky?: string; en?: string };
  eventDate?: string | null;
  isActiveDefault?: boolean;
}

interface Partner {
  id: string;
  kind: 'company' | 'individual';
  name: { ru?: string; ky?: string; en?: string } | string;
  logoUrl: string | null;
  website: string | null;
  instagramUrl: string | null;
  description: { ru?: string; ky?: string; en?: string } | string | null;
  totalTrees: number;
  rank: number;
}

type DonorTab = 'rt' | 'forest';
type PartnerTab = 'top' | 'mid';

const DONOR_TAB_TO_TYPE: Record<DonorTab, string> = {
  rt: 'recent',
  forest: 'community'
};

const PARTNER_TAB_TO_RANGE: Record<PartnerTab, { minTrees: number; maxTrees?: number }> = {
  top: { minTrees: 1000 },
  mid: { minTrees: 400, maxTrees: 1000 }
};

const MEDAL: Record<number, string> = { 1: '#E4C04E', 2: '#C9D0D8', 3: '#D69A62' };
const MEDAL_TEXT: Record<number, string> = { 1: '#B8860B', 2: '#8B959F', 3: '#A5642E' };

const ResultsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState<Stats>({ totalTrees: 0, totalPlanted: 0, totalDonors: 0 });
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [donorsEventId, setDonorsEventId] = useState<string>('all');
  const [partnersEventId, setPartnersEventId] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ events: Festival[] }>(`${API_URL}/api/events`);
        setFestivals(data.events);
        const defaultEvent = data.events.find(e => e.isActiveDefault);
        if (!defaultEvent) return;

        const defaultId = String(defaultEvent.id);
        setDonorsEventId(defaultId);

        // Партнёры: если на активном событии нет ни одного партнёра
        // (>= 1000 деревьев — таб top), автоматически откатываемся
        // на предыдущее по дате событие, где партнёры есть.
        const hasPartners = async (eventId: number) => {
          try {
            const { data } = await axios.get<{ companies: unknown[] }>(
              `${API_URL}/api/companies/rating`,
              { params: { limit: 1, offset: 0, minTrees: 1000, eventId } }
            );
            return data.companies.length > 0;
          } catch {
            return false;
          }
        };

        if (await hasPartners(defaultEvent.id)) {
          setPartnersEventId(defaultId);
          return;
        }

        const defaultDate = defaultEvent.eventDate ? new Date(defaultEvent.eventDate).getTime() : 0;
        const earlier = data.events
          .filter(e => e.id !== defaultEvent.id && e.eventDate)
          .filter(e => new Date(e.eventDate as string).getTime() < defaultDate)
          .sort((a, b) => new Date(b.eventDate as string).getTime() - new Date(a.eventDate as string).getTime());

        for (const e of earlier) {
          if (await hasPartners(e.id)) {
            setPartnersEventId(String(e.id));
            return;
          }
        }

        setPartnersEventId(defaultId);
      } catch (err) {
        console.error('festivals err', err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<Stats>(`${API_URL}/api/donations/stats`);
        setStats({
          totalTrees: data.totalTrees || 0,
          totalPlanted: data.totalPlanted || 0,
          totalDonors: data.totalDonors || 0
        });
      } catch (err) {
        console.error('stats err', err);
      }
    })();
  }, []);

  return (
    <section id="results" style={{ background: '#DFEFCB', padding: '64px 24px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <span className="section-tag">{t('results.tag', 'Наши результаты')}</span>
        </div>
        <h2 className="h2-center" style={{ marginBottom: 34 }}>
          {t('results.title', 'Вместе мы уже сделали много')}
        </h2>

        <div className="grid-3 stat-cards">
          <StatCard
            value={stats.totalPlanted}
            label={t('results.trees_all_time', 'всего посажено деревьев')}
            rotate={-1}
          />
          <StatCard
            value={stats.totalTrees}
            label={t('results.supported', 'нас поддержали')}
            rotate={0}
          />
          <StatCard
            value={stats.totalDonors}
            label={t('results.guardians', 'хранители природы')}
            rotate={1}
          />
        </div>

        <BlockHeader
          title={t('results.donors_title', 'Сделавшие вклад')}
          festivals={festivals}
          eventId={donorsEventId}
          onEventIdChange={setDonorsEventId}
          marginTop={48}
        />
        <BlockCard background="#FFFFFF" marginTop={0}>
          <DonorsBlock eventId={donorsEventId} />
        </BlockCard>

        <BlockHeader
          title={t('partners_rating.title', 'Наши партнёры')}
          festivals={festivals}
          eventId={partnersEventId}
          onEventIdChange={setPartnersEventId}
          marginTop={40}
        />
        <BlockCard background="#FFFFFF" marginTop={0}>
          <PartnersBlock eventId={partnersEventId} />
        </BlockCard>
      </div>
    </section>
  );
};

const BlockHeader: React.FC<{
  title: string;
  festivals: Festival[];
  eventId: string;
  onEventIdChange: (v: string) => void;
  marginTop: number;
}> = ({ title, festivals, eventId, onEventIdChange, marginTop }) => {
  const { t, i18n } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginTop,
        marginBottom: 10
      }}
    >
      <h3 style={{ font: "700 24px 'Montserrat Alternates', sans-serif", color: '#0B5E36', margin: 0 }}>
        {title}
      </h3>
      <select
        className="f block-header-select"
        value={eventId}
        onChange={e => onEventIdChange(e.target.value)}
      >
        <option value="all">{t('partners_rating.all_events', 'Все фестивали')}</option>
        {festivals.map(f => (
          <option key={f.slug} value={f.id}>
            {getLocalized(f.name, i18n.language)}
          </option>
        ))}
      </select>
    </div>
  );
};

const BlockCard: React.FC<{ background: string; marginTop: number; children: React.ReactNode }> = ({ background, marginTop, children }) => (
  <div
    style={{
      background,
      borderRadius: 28,
      padding: 16,
      marginTop,
      boxShadow: '0 4px 20px rgba(45,80,22,.05)'
    }}
  >
    {children}
  </div>
);

// ============================================================
// BLOCK 1 — INDIVIDUAL DONORS
// ============================================================

const DonorsBlock: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<DonorTab>('rt');
  const [donors, setDonors] = useState<Donor[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadDonors = React.useCallback(
    async (reset: boolean) => {
      const off = reset ? 0 : offset;
      setLoading(true);
      try {
        const params: any = { limit: 10, offset: off, type: DONOR_TAB_TO_TYPE[tab] };
        if (eventId !== 'all') params.eventId = eventId;
        const { data } = await axios.get<{ donors: Donor[]; hasMore: boolean }>(
          `${API_URL}/api/donations/recent`,
          { params }
        );
        setDonors(prev => (reset ? data.donors : [...prev, ...data.donors]));
        setHasMore(!!data.hasMore);
        setOffset(off + data.donors.length);
      } catch (err) {
        console.error('donors err', err);
      } finally {
        setLoading(false);
      }
    },
    [tab, eventId, offset]
  );

  useEffect(() => {
    loadDonors(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, eventId]);

  const donorName = (d: Donor) => {
    if (d.isAnonymous) return t('donations.anonymous', 'Анонимно');
    if (d.organizationName) return d.organizationName;
    return [d.firstName, d.lastName].filter(Boolean).join(' ') || t('donations.anonymous', 'Анонимно');
  };

  const fmtDate = (s: string) => {
    const d = new Date(s);
    if (tab === 'rt') {
      return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const instaHandle = (u?: string) => {
    if (!u) return null;
    const m = u.match(/instagram\.com\/([a-zA-Z0-9_.]+)/);
    return m ? '@' + m[1] : u;
  };

  return (
    <div>
      <div className="donor-tabs" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 18, maxWidth: 600 }}>
          <TabButton active={tab === 'rt'} onClick={() => setTab('rt')}>
            {t('donations.filter_recent')}
          </TabButton>
          <TabButton active={tab === 'forest'} onClick={() => setTab('forest')}>
            {t('donations.filter_community')}
          </TabButton>
        </div>

        <div className="grid-2">
          {donors.map(d => (
            <div
              key={d.id}
              style={{
                background: '#fff',
                borderRadius: 18,
                boxShadow: '0 4px 16px rgba(45,80,22,.07)',
                padding: '13px 17px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ font: "600 15.5px 'Golos Text',sans-serif" }}>{donorName(d)}</div>
                {d.instagramUrl && (
                  <a
                    href={d.instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ font: "400 13px 'Golos Text',sans-serif" }}
                  >
                    {instaHandle(d.instagramUrl)}
                  </a>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ font: "400 11.5px 'Golos Text',sans-serif", color: '#999' }}>
                  {fmtDate(d.paidAt)}
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: '#DFEFCB',
                    borderRadius: 999,
                    padding: '4px 10px',
                    marginTop: 4
                  }}
                >
                  <img src="/uploads/tree-icon.svg" alt="" style={{ width: 17, height: 17 }} />
                  <b style={{ font: "600 14px 'Golos Text',sans-serif", color: '#0B7A43' }}>
                    {d.treesCount.toLocaleString('ru-RU')}
                  </b>
                </div>
              </div>
            </div>
          ))}
        </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="ghost-btn" onClick={() => loadDonors(false)} disabled={loading}>
            {loading ? t('donations.loading') : t('donations.load_more')}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BLOCK 2 — PARTNERS RATING (formerly PartnersRatingSection)
// ============================================================

const PartnersBlock: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<PartnerTab>('top');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadPartners = React.useCallback(
    async (reset: boolean) => {
      const off = reset ? 0 : offset;
      const range = PARTNER_TAB_TO_RANGE[tab];
      setLoading(true);
      try {
        const params: any = { limit: 10, offset: off, minTrees: range.minTrees };
        if (range.maxTrees) params.maxTrees = range.maxTrees;
        if (eventId !== 'all') params.eventId = eventId;
        const { data } = await axios.get<{ companies: Partner[]; hasMore: boolean }>(
          `${API_URL}/api/companies/rating`,
          { params }
        );
        setPartners(prev => (reset ? data.companies : [...prev, ...data.companies]));
        setHasMore(!!data.hasMore);
        setOffset(off + data.companies.length);
      } catch (err) {
        console.error('partners err', err);
      } finally {
        setLoading(false);
      }
    },
    [tab, eventId, offset]
  );

  useEffect(() => {
    loadPartners(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, eventId]);

  const instaHandle = (u: string | null) => {
    if (!u) return null;
    const m = u.match(/instagram\.com\/([a-zA-Z0-9_.]+)/);
    return m ? '@' + m[1] : u;
  };
  const siteDisplay = (u: string | null) =>
    u ? u.replace(/^https?:\/\//, '').replace(/\/$/, '') : null;

  const displayName = (p: Partner) =>
    typeof p.name === 'string' ? p.name : getLocalized(p.name, i18n.language);

  const displayDesc = (p: Partner) => {
    if (!p.description) return '';
    return typeof p.description === 'string'
      ? p.description
      : getLocalized(p.description, i18n.language);
  };

  return (
    <div>
      <div className="donor-tabs" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 26, maxWidth: 600 }}>
        <TabButton active={tab === 'top'} onClick={() => setTab('top')}>
          {t('partners_rating.tab_top', 'от 1000 деревьев и выше')}
        </TabButton>
        <TabButton active={tab === 'mid'} onClick={() => setTab('mid')}>
          {t('partners_rating.tab_mid', 'от 400 деревьев и выше')}
        </TabButton>
      </div>

      <div className="grid-2">
        {partners.map(p => (
          <PartnerCard
            key={`${p.kind}-${p.id}`}
            p={p}
            name={displayName(p)}
            description={displayDesc(p)}
            instaHandle={instaHandle}
            siteDisplay={siteDisplay}
          />
        ))}
      </div>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 26 }}>
          <button className="ghost-btn" onClick={() => loadPartners(false)} disabled={loading}>
            {loading ? t('donations.loading') : t('donations.load_more')}
          </button>
        </div>
      )}
    </div>
  );
};

const PartnerCard: React.FC<{
  p: Partner;
  name: string;
  description: string;
  instaHandle: (u: string | null) => string | null;
  siteDisplay: (u: string | null) => string | null;
}> = ({ p, name, description, instaHandle, siteDisplay }) => {
  const topRank = p.rank <= 3;
  const color = topRank ? MEDAL[p.rank] : null;
  const textCol = topRank ? MEDAL_TEXT[p.rank] : null;
  const insta = instaHandle(p.instagramUrl);
  const site = siteDisplay(p.website);
  const displayLogo = p.logoUrl;

  return (
    <div
      className="card-hover"
      style={{
        background: '#fff',
        borderRadius: 22,
        boxShadow: topRank
          ? `0 6px 24px rgba(45,80,22,.08), inset 0 0 0 1.5px ${color}`
          : '0 6px 24px rgba(45,80,22,.08)',
        padding: 10,
        position: 'relative'
      }}
    >
      {topRank ? (
        <div
          style={{
            position: 'absolute',
            top: -16,
            left: -6,
            width: 48,
            height: 48,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.15))'
          }}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src="/uploads/award-lean.svg"
              alt=""
              style={{ width: '100%', height: '100%', filter: getMedalFilter(p.rank) }}
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                font: `800 18px 'Golos Text', sans-serif`,
                color: textCol as string
              }}
            >
              {p.rank}
            </span>
          </div>
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            top: -12,
            left: -10,
            width: 38,
            height: 38,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: "700 16px 'Montserrat Alternates', sans-serif",
            background: '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,.12), inset 0 0 0 1.5px #DCE8CF',
            color: '#7d8a70',
            pointerEvents: 'none'
          }}
        >
          {p.rank}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 10,
            background: '#fff',
            boxShadow: 'inset 0 0 0 1px #DCE8CF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          {displayLogo ? (
            <img
              src={displayLogo}
              alt=""
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ font: "700 18px 'Montserrat Alternates', sans-serif", color: '#A9D97C' }}>
              {name.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                className="partner-name"
                style={{ font: "600 17px 'Golos Text', sans-serif" }}
              >
                {name}
              </div>
              {insta && (
                <div style={{ font: "400 13px 'Golos Text', sans-serif", color: '#777', marginTop: 2 }}>
                  <a href={p.instagramUrl || '#'} target="_blank" rel="noreferrer">
                    {insta}
                  </a>
                </div>
              )}
              {site && (
                <div style={{ font: "400 13px 'Golos Text', sans-serif", color: '#777', marginTop: 1 }}>
                  <a href={p.website || '#'} target="_blank" rel="noreferrer">
                    {site}
                  </a>
                </div>
              )}
            </div>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: '#DFEFCB',
                borderRadius: 999,
                padding: '6px 10px'
              }}
            >
              <img src="/uploads/tree-icon.svg" alt="" style={{ width: 18, height: 18 }} />
              <span style={{ font: "600 14px 'Golos Text', sans-serif", color: '#0B7A43' }}>
                {p.totalTrees.toLocaleString('ru-RU')}
              </span>
            </div>
          </div>
          {description && (
            <div style={{ font: "400 12.5px/1.45 'Golos Text', sans-serif", color: '#999', marginTop: 8 }}>
              {description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getMedalFilter(rank: number) {
  if (rank === 1)
    return 'invert(74%) sepia(58%) saturate(451%) hue-rotate(2deg) brightness(93%) contrast(89%)';
  if (rank === 2)
    return 'invert(88%) sepia(4%) saturate(273%) hue-rotate(178deg) brightness(93%) contrast(87%)';
  return 'invert(66%) sepia(30%) saturate(507%) hue-rotate(346deg) brightness(90%) contrast(89%)';
}

// ============================================================
// SHARED COMPONENTS
// ============================================================

const StatCard: React.FC<{ value: number; label: string; rotate: number }> = ({ value, label, rotate }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 22,
      boxShadow: '0 6px 24px rgba(45,80,22,.08)',
      padding: 28,
      textAlign: 'center',
      transform: `rotate(${rotate}deg)`
    }}
  >
    <img src="/uploads/fig/leaf.png" alt="" style={{ height: 42, display: 'block', margin: '0 auto' }} />
    <div style={{ font: "700 38px 'Montserrat Alternates', sans-serif", color: '#0B7A43', marginTop: 6 }}>
      {value.toLocaleString('ru-RU')}
    </div>
    <div style={{ font: "500 15px 'Golos Text', sans-serif", color: '#5a6b4d', marginTop: 4 }}>
      {label}
    </div>
  </div>
);

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children
}) => (
  <button
    type="button"
    onClick={onClick}
    className={active ? 'active' : ''}
    style={{
      border: 0,
      borderRadius: 9999,
      background: active ? '#0B7A43' : '#fff',
      boxShadow: active ? 'none' : 'inset 0 0 0 1.5px #DCE8CF',
      color: active ? '#fff' : '#5a6b4d',
      font: "500 13px 'Golos Text', sans-serif",
      padding: '10px 12px',
      cursor: 'pointer',
      transition: 'all 150ms',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'center'
    }}
  >
    {children}
  </button>
);

export default ResultsSection;
